import { CountryCode } from '@/enums';
import { getCurrencySymbol } from '@/helpers/AmountHelper';
import { getCountryCode } from '@/helpers/CountryCodeHelper';
import { store } from '@/store';
import { InitPaymentRequest } from '@/types/Payment';
import { CreatePaymentRequest } from '@/types/Payment/CreatePayment';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInterceptorInstance from './axiosInterceptorInstance';

export const getPaymentType = createAsyncThunk(
  'payment/getPaymentType',
  async ({
    agentCode,
    entityType,
  }: {
    agentCode: string;
    entityType: number;
  }) => {
    try {
      if (agentCode === undefined) {
        agentCode = '';
      }
      const relativeUrl = `Payment/GetPaymentType`;
      const rootState = store.getState();
      const affilitator = rootState.affilitator;
      if (affilitator.agentId === undefined || affilitator.agentId === null) {
        const queryString = `?agentCode=${agentCode}%entityType=${entityType}`;
        const apiUrl = `/application/?relativeUrl=${relativeUrl}&queryString=${queryString}`;
        const response = await axiosInterceptorInstance.get(apiUrl);
        return response.data;
      } else {
        const queryString = `?agentCode=${agentCode}%entityType=${entityType}%affiliateId=${affilitator.agentId}`;
        const apiUrl = `/application/?relativeUrl=${relativeUrl}&queryString=${queryString}`;
        const response = await axiosInterceptorInstance.get(apiUrl);
        return response.data;
      }
    } catch (error) {
      return error;
    }
  },
);

const createReferenceNumber = (encodedOrderId: string) => {
  return encodedOrderId ? encodedOrderId + '_' + Date.now().toString() : '';
};

export const initPayment = createAsyncThunk(
  'payment/init',
  async ({
    redirectPath,
    entityType,
    language,
    productCategory,
  }: {
    redirectPath: string;
    entityType: number;
    language: string;
    productCategory: string;
  }) => {
    const rootState = store.getState();
    const personalDetail = rootState.personalDetail;
    const planSummary = rootState.planSummary;
    const countryCode = getCountryCode();

    try {
      const planDescription = [
        rootState.uiState.productPlanClass?.find(
          (x) => x.planClassId === planSummary.planClass,
        )?.description,
        'Plan',
      ].join(' ');

      const request: InitPaymentRequest = {
        referenceNo: createReferenceNumber(
          planSummary.encodedOrderId,
        ).substring(0, 60),
        paymentType: planSummary.payMode,
        amount: planSummary?.priceBreakdown?.totalPremium || 0,
        description: planDescription,
        isExistingCustomer: personalDetail.adultDetails[0].isExistingCustomer,
        identityNumber: personalDetail.adultDetails[0].identityNumber,
        customerName: personalDetail.adultDetails[0].fullName.includes('*')
          ? ''
          : personalDetail.adultDetails[0].fullName,
        email: personalDetail.adultDetails[0].email.includes('*')
          ? ''
          : personalDetail.adultDetails[0].email,
        phone: personalDetail.adultDetails[0].mobileNumber.includes('*')
          ? ''
          : personalDetail.adultDetails[0].mobileNumber,
        productCategory,
        encodedOrderId: planSummary.encodedOrderId,
        language,
        entityType,
        countryCode,
        successUrl: '',
        errorUrl: '',
        ...(countryCode === CountryCode.Philippines
          ? {
              lineItems: [
                {
                  currency: getCurrencySymbol(),
                  amount: planSummary?.priceBreakdown?.totalPremium || 0,
                  name: planDescription,
                  quantity: 1,
                },
              ],
            }
          : {}),
      };

      const relativeUrl = 'Payment';
      const apiUrl = `/application/${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const createPayment = (
  transactionNo: string,
  encodedOrderId: string,
) => {
  return async () => {
    try {
      const rootState = store.getState();
      const planSummary = rootState.planSummary;
      if (
        planSummary.priceBreakdown &&
        planSummary.priceBreakdown?.totalPremium > 0
      ) {
        const request: CreatePaymentRequest = {
          encodedOrderId,
          amount: planSummary.priceBreakdown?.totalPremium || 0,
          paymentType: planSummary.payMode,
          transactionNo,
        };
        const relativeUrl = 'Payment';
        const apiUrl = `/application/?relativeUrl=${relativeUrl}`;
        const response = await axiosInterceptorInstance.post(apiUrl, request);
        return response.data;
      }
    } catch (error) {
      return error;
    }
  };
};

export const getPaymentTransactionNumber = createAsyncThunk(
  'payment/getPaymentTransactionNumber',
  async (encodedOrderId: string) => {
    try {
      const queryString = `?encodedOrderId=${encodedOrderId}`;
      const relativeUrl = 'Payment/GetPaymentTransactionNumber';
      const apiUrl = `/application/?relativeUrl=${relativeUrl}&queryString=${queryString}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const getPaymentStatus = createAsyncThunk(
  'payment/getPaymentStatus',
  async ({
    transactionNo,
    entityType,
  }: {
    transactionNo: string;
    entityType: number;
  }) => {
    try {
      const relativeUrl = `Payment/GetPaymentStatus`;
      const queryString = `?transactionNo=${transactionNo}%entityType=${entityType}`;
      const apiUrl = `/application/?relativeUrl=${relativeUrl}&queryString=${queryString}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);
